import { SelectChangeEvent } from '@mui/material';
import { Dispatch, SetStateAction } from 'react';
import { TFunction } from 'react-i18next';
import {
  PostSchedule,
  PostFrequency,
  PageOptions,
  FacebookPost,
  GroupOptions,
} from 'src/DTO/FacebookPost.type';
import { PageGroupOptions, DEFAULTS } from 'src/pages/Facebook/components/constants';
import { GroupData, PageData } from 'src/pages/Facebook/components/types';

interface FormattedDateDetails {
  formattedStartDate: string;
  formattedEndDate: string;
  formattedTime: string;
}

export const formatDateDetails = (
  startDate: string,
  endDate: string,
  time: string,
): FormattedDateDetails => {
  const formattedStartDate = new Date(startDate).toLocaleDateString('hu-HU');
  const formattedEndDate = new Date(endDate).toLocaleDateString('hu-HU');

  const [hours, minutes] = time.split(':');

  const formattedTime = `${hours}:${minutes}`;

  return { formattedStartDate, formattedEndDate, formattedTime };
};

export const getPostScheduleDescription = (postSchedule: PostSchedule): string => {
  const { startDate, startTime, frequency, endDate } = postSchedule;

  const frequencyLabels = {
    [PostFrequency.WEEKLY]: 'Hetente',
    [PostFrequency.DAILY]: 'Naponta',
    [PostFrequency.EVERY_SECOND_DAY]: 'Két naponta',
  };

  const frequencyLabel = frequencyLabels[frequency];

  if (!startDate || !startTime || !frequency || !endDate || !frequencyLabel) return '';

  const { formattedStartDate, formattedEndDate, formattedTime } = formatDateDetails(
    startDate,
    endDate,
    startTime,
  );

  return `${frequencyLabel}: ${formattedStartDate}, ${formattedTime} - ${formattedEndDate}, ${formattedTime}`;
};

export const getURL = (value: PageOptions | GroupOptions): string => {
  return PageGroupOptions[value];
};

export const handlePageSelection = (
  event: SelectChangeEvent<string[]>,
  populatedData: PageData[],
  defaultOption: string,
  setData: Dispatch<SetStateAction<PageData[]>>,
) => {
  const { PAGE } = DEFAULTS;

  const dataArray = Array.isArray(event.target.value) ? event.target.value : [event.target.value];

  if (!dataArray.includes(defaultOption)) return;

  const defaultPageId = PAGE.find(p => p.name === PageOptions.DELLA_DIAKMUNKA)?.id ?? '';

  const selectedItems = dataArray.map(selectedName => {
    const originalItem = populatedData?.find(p => p.name === selectedName);
    return {
      id: originalItem?.id ?? defaultPageId,
      name: selectedName,
      url: originalItem?.url ?? getURL(selectedName as PageOptions),
    };
  });

  setData([...selectedItems]);
};

export const handleGroupSelection = (
  event: SelectChangeEvent<string[]>,
  populatedData: GroupData[],
  setData: Dispatch<SetStateAction<GroupData[]>>,
) => {
  const dataArray = Array.isArray(event.target.value) ? event.target.value : [event.target.value];

  const selectedItems = dataArray.map(selectedName => {
    const originalItem = populatedData?.find(p => p.name === selectedName);
    return {
      name: selectedName,
      url: originalItem?.url ?? getURL(selectedName as GroupOptions),
    };
  });

  setData([...selectedItems]);
};

export const isPostScheduleExpired = (post: PostSchedule): boolean => {
  if (!post || !post?.endDate || !post?.startTime) return false;

  const today = new Date();

  const postEndDate = new Date(post.endDate).toISOString().split('T')[0];
  const postTime = post.startTime;

  const hours = String(today.getHours()).padStart(2, '0');
  const minutes = String(today.getMinutes()).padStart(2, '0');

  const currentDate = today.toISOString().split('T')[0];
  const currentTime = `${hours}:${minutes}`;

  const isPostEndingToday = currentDate === postEndDate;

  return isPostEndingToday ? currentTime >= postTime : currentDate > postEndDate;
};

export const calculateDateDetails = (startDate: string): Date => {
  const newDate = startDate ? new Date(startDate) : new Date();

  const endDate = new Date(newDate);
  endDate.setDate(newDate.getDate() + 30);

  return endDate;
};

export const isPostTextUnchanged = (
  previousPosts: FacebookPost[],
  currentPosts: FacebookPost[],
  index: number,
): boolean => {
  if (!previousPosts.length || !currentPosts.length) return false;

  return previousPosts.some(post => post.postText?.trim() === currentPosts[index].postText?.trim());
};

export const validateFacebookImage = (
  file: { size: number; type: string },
  t: TFunction,
): {
  valid: boolean;
  message: string;
} => {
  const maxSize = 5 * 1024 * 1024;
  const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];

  if (file.size > maxSize) {
    return {
      valid: false,
      message: t('photoUpload.alerts.fileSizeTooLarge'),
    };
  }

  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      message: t('photoUpload.alerts.incorrectFileType'),
    };
  }

  return { valid: true, message: '' };
};
